export const nodeApiExample = `const axios = require('axios');
const crypto = require('crypto');
const { v4: uuidv4 } = require('uuid');

// Configuration
const API_BASE_URL = 'https://app.reasonshield.com/api/v1';
const API_ENDPOINT_PATH = '/lis-webhook/vendor';
const API_KEY = process.env.VENDOR_API_KEY;
const WEBHOOK_SECRET = process.env.VENDOR_WEBHOOK_SECRET;
const VENDOR_ID = process.env.VENDOR_ID;

/**
 * Generate HMAC signature for payload
 * @param {Object} payload - Request payload to sign
 * @param {string} secret - Webhook secret key
 * @returns {string} Hex-encoded signature
 */
function generateSignature(payload, secret) {
  // Create canonical JSON string (sorted keys, no whitespace)
  const payloadString = JSON.stringify(payload, Object.keys(payload).sort());
  
  // Create HMAC-SHA256 signature
  const hmac = crypto.createHmac('sha256', secret);
  hmac.update(payloadString);
  return hmac.digest('hex');
}

/**
 * Submit data to the API with dual authentication
 * @param {Object} testData - Test data to submit
 * @returns {Promise<Object>} API response
 */
async function submitData(testData) {
  // Create full request payload
  const payload = {
    vendor_id: VENDOR_ID,
    timestamp: new Date().toISOString(),
    request_id: uuidv4(),
    test_data: testData
  };
  
  // Generate signature
  const signature = generateSignature(payload, WEBHOOK_SECRET);
  
  // Set headers with both authentication methods
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': \`Bearer \${API_KEY}\`,        // API Key Authentication
    'X-Vendor-Signature': signature                // Signature Verification
  };
  
  try {
    const url = \`\${API_BASE_URL}\${API_ENDPOINT_PATH}/submit-data\`;
    const response = await axios.post(url, payload, { headers });
    return response.data;
  } catch (error) {
    throw new Error(\`Error submitting data: \${error.response?.data?.message || error.message}\`);
  }
}

/**
 * Check processing status of submitted data
 * @param {string} dataId - ID of the submitted data
 * @returns {Promise<Object>} Status information
 */
async function checkStatus(dataId) {
  const headers = {
    'Authorization': \`Bearer \${API_KEY}\`
  };
  
  try {
    const url = \`\${API_BASE_URL}\${API_ENDPOINT_PATH}/status/\${dataId}\`;
    const response = await axios.get(url, { headers });
    return response.data;
  } catch (error) {
    throw new Error(\`Error checking status: \${error.response?.data?.message || error.message}\`);
  }
}

/**
 * Get report for processed data
 * @param {string} dataId - ID of the submitted data
 * @returns {Promise<Object>} Report data
 */
async function getReport(dataId) {
  const headers = {
    'Authorization': \`Bearer \${API_KEY}\`
  };
  
  try {
    const url = \`\${API_BASE_URL}\${API_ENDPOINT_PATH}/report/\${dataId}\`;
    const response = await axios.get(url, { headers });
    return response.data;
  } catch (error) {
    throw new Error(\`Error retrieving report: \${error.response?.data?.message || error.message}\`);
  }
}

// Example usage with async/await
async function main() {
  try {
    // Create test data
    const testData = {
      test_id: '12345',
      panel_type: 'RESPIRATORY',
      patient_info: {
        patient_id: 'PT-789456',
        age: 45,
        gender: 'M'
      },
      collection_info: {
        collection_date: '2024-09-10T13:11:00-06:00',
        specimen_type: 'nasopharyngeal swab'
      },
      test_details: {
        viruses: [
          {name: 'Influenza A', result: 'Not Detected'},
          {name: 'SARS-CoV-2', result: 'Detected'}
        ],
        bacteria: [
          {name: 'Streptococcus pneumoniae', result: 'Not Detected'},
          {name: 'Mycoplasma pneumoniae', result: 'Not Detected'}
        ]
      }
    };
    
    // Submit the data
    const result = await submitData(testData);
    console.log(\`Data submitted successfully. ID: \${result.data_id}\`);
    
    // Get the data ID
    const dataId = result.data_id;
    
    // In a real implementation, you would wait or poll for completion
    // For demonstration, we'll just check once
    const statusResult = await checkStatus(dataId);
    console.log(\`Status: \${statusResult.status}\`);
    
    // If processing is complete and report is available, get the report
    if (statusResult.status === 'COMPLETED' && statusResult.report_available) {
      const report = await getReport(dataId);
      console.log(\`Retrieved report with risk assessment: \${report.report_data.risk_assessment.overall_risk}\`);
    }
    
  } catch (error) {
    console.error(\`Error: \${error.message}\`);
  }
}

main();`;
 